import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, BuildingIcon, ShoppingCartIcon, ZapIcon, ArrowDownLeftIcon, CoffeeIcon, WifiIcon, CarIcon, UtensilsIcon, ArrowUpRightIcon } from 'lucide-react';
interface TransactionsScreenProps {
  onBack: () => void;
}
type FilterType = 'all' | 'incoming' | 'outgoing';
const mockTransactions = [{
  id: 1,
  title: 'Salary Credit',
  date: 'Today, 09:00 AM',
  amount: '250,000.00',
  type: 'incoming',
  icon: BuildingIcon,
  iconBg: '#10B981'
}, {
  id: 2,
  title: 'Grocery Store',
  date: 'Yesterday, 06:30 PM',
  amount: '12,450.00',
  type: 'outgoing',
  icon: ShoppingCartIcon,
  iconBg: '#F59E0B'
}, {
  id: 3,
  title: 'Electric Bill',
  date: 'Oct 24, 10:15 AM',
  amount: '8,200.00',
  type: 'outgoing',
  icon: ZapIcon,
  iconBg: '#EF4444'
}, {
  id: 4,
  title: 'Ali Transfer',
  date: 'Oct 23, 02:45 PM',
  amount: '5,000.00',
  type: 'incoming',
  icon: ArrowDownLeftIcon,
  iconBg: '#10B981'
}, {
  id: 5,
  title: 'Coffee Shop',
  date: 'Oct 22, 08:20 AM',
  amount: '850.00',
  type: 'outgoing',
  icon: CoffeeIcon,
  iconBg: '#8B5CF6'
}, {
  id: 6,
  title: 'Internet Bill',
  date: 'Oct 20, 11:00 AM',
  amount: '3,500.00',
  type: 'outgoing',
  icon: WifiIcon,
  iconBg: '#3B82F6'
}, {
  id: 7,
  title: 'Freelance Work',
  date: 'Oct 18, 04:30 PM',
  amount: '45,000.00',
  type: 'incoming',
  icon: ArrowDownLeftIcon,
  iconBg: '#10B981'
}, {
  id: 8,
  title: 'Fuel Station',
  date: 'Oct 15, 07:10 PM',
  amount: '6,000.00',
  type: 'outgoing',
  icon: CarIcon,
  iconBg: '#F59E0B'
}, {
  id: 9,
  title: 'Restaurant',
  date: 'Oct 12, 09:45 PM',
  amount: '4,200.00',
  type: 'outgoing',
  icon: UtensilsIcon,
  iconBg: '#EC4899'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 12
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
export function TransactionsScreen({
  onBack
}: TransactionsScreenProps) {
  const [filter, setFilter] = useState<FilterType>('all');
  const filteredTransactions = mockTransactions.filter(t => {
    if (filter === 'all') return true;
    return t.type === filter;
  });
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} className="w-full min-h-screen pb-24" style={{
    backgroundColor: '#FFF5F0'
  }} data-id="element-1071">
      {/* Header */}
      <div className="px-6 pt-6 pb-4 flex items-center gap-4 bg-white shadow-sm sticky top-0 z-10" data-id="element-1072">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-black/5 transition-colors" data-id="element-1073">
          <ArrowLeftIcon className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2} data-id="element-1074" />
        </button>
        <h1 className="text-xl font-bold text-[#1A1A1A]" data-id="element-1075">Transactions</h1>
      </div>

      <div className="px-6 py-6 space-y-6" data-id="element-1076">
        {/* Filters */}
        <div className="flex items-center gap-3" data-id="element-1077">
          {(['all', 'incoming', 'outgoing'] as FilterType[]).map(f => <button key={f} onClick={() => setFilter(f)} className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-colors ${filter === f ? 'bg-[#D95A1A] text-white shadow-md' : 'bg-white text-[#8A7B72] hover:bg-white/80'}`} data-id="element-1078">
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>)}
        </div>

        {/* Transactions List */}
        <motion.div variants={stagger} initial="hidden" animate="show" className="bg-white rounded-[24px] p-4 shadow-sm" data-id="element-1079">
          {filteredTransactions.length > 0 ? <div className="space-y-1" data-id="element-1080">
              {filteredTransactions.map(transaction => {
            const Icon = transaction.icon;
            const isIncoming = transaction.type === 'incoming';
            return <motion.div key={transaction.id} variants={fadeUp} className="flex items-center gap-4 p-3 rounded-2xl hover:bg-[#FFF5F0] transition-colors" data-id="element-1081">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
                backgroundColor: `${transaction.iconBg}15`
              }} data-id="element-1082">
                      <Icon className="w-6 h-6" style={{
                  color: transaction.iconBg
                }} strokeWidth={2} data-id="element-1083" />
                    </div>

                    <div className="flex-1 min-w-0" data-id="element-1084">
                      <h4 className="text-[15px] font-bold text-[#1A1A1A] mb-0.5 truncate" data-id="element-1085">
                        {transaction.title}
                      </h4>
                      <p className="text-[12px] text-[#8A7B72]" data-id="element-1086">
                        {transaction.date}
                      </p>
                    </div>

                    <div className="text-right shrink-0" data-id="element-1087">
                      <p className={`text-[15px] font-bold mb-0.5 ${isIncoming ? 'text-[#10B981]' : 'text-[#1A1A1A]'}`} data-id="element-1088">
                        {isIncoming ? '+' : '-'}Rs {transaction.amount}
                      </p>
                      <p className="text-[11px] font-medium text-[#8A7B72] flex items-center justify-end gap-1" data-id="element-1089">
                        {isIncoming ? <ArrowDownLeftIcon className="w-3 h-3 text-[#10B981]" data-id="element-1090" /> : <ArrowUpRightIcon className="w-3 h-3 text-[#8A7B72]" data-id="element-1091" />}
                        {isIncoming ? 'Received' : 'Sent'}
                      </p>
                    </div>
                  </motion.div>;
          })}
            </div> : <div className="py-12 text-center" data-id="element-1092">
              <p className="text-[#8A7B72] text-[15px] font-medium" data-id="element-1093">
                No transactions found
              </p>
            </div>}
        </motion.div>
      </div>
    </motion.div>;
}